import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { Selector } from "@/components/Selector"
import { ITableHead, Table } from "@/components/Table"
import { Title } from "@/components/Title"
import { isRainProvider } from "@/constants/ProviderConstants"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { IOption } from "@/types"
import { DepositType, useCurrencyTable, useTopUpStore } from "@usher/pe-client-front-end-lib"
import { clsx } from "clsx"
import { Fragment, useEffect, useMemo, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import "./style.sass"

interface ICurrencyTableItemProps {
  onDepositClick: (currency: string, spend: number) => void
  filterValue: string
  loading?: string
  onChange: (value: string) => void
  isOrder?: boolean
  depositType?: DepositType
}
export interface IECurrency {
  symbol: string
  name: string
  fee: number
  completion: number
  spend: string
}
export const getCoinIcon = (coinName: string) => {
  if (coinName.startsWith("USDC")) return "usdc"
  if (coinName.startsWith("USD")) return "usdt"
  return coinName.toLowerCase()
}
const Row = ({ title, value }: { title: string; value: string | number }) => {
  return (
    <div className="flex flex-col gap-2 mini:flex-row mini:justify-between xs:justify-start">
      <span className="font-[ChakraPetchBold] text-sm">{title}:</span>
      <div className="font-bold text-sm mini:text-right xs:text-left">{value}</div>
    </div>
  )
}

const TABLE_HEADS: ITableHead[] = [
  {
    key: "currency",
  },
  {
    key: "percent",
    label_id: "fee%",
  },
  {
    key: "fee",
    label_id: "fee",
  },
  {
    key: "completion",
    label_id: "completion",
  },
  {
    key: "spend",
    label_id: "amount-in-crypto",
  },
  {
    key: "action",
  },
]
export const CurrencyTable = ({ depositType, isOrder, onChange, onDepositClick, filterValue = "", loading }: ICurrencyTableItemProps) => {
  const { $t: t } = useIntl()
  const [value, setValue] = useState<IOption>()
  const isCreateCard = isOrder && depositType === DepositType.Card
  const isMobile = useMediaQuery("(max-width: 768px)")
  const { pricesLoading, card } = useTopUpStore((state) => ({
    pricesLoading: state.pricesLoading,
    card: state.card,
  }))
  const isRainCard = isRainProvider(card?.provider_id)
  const { tableData } = useCurrencyTable(depositType, isOrder)
  const { allPrices } = useMemo(() => {
    const buffer = [...tableData]
    const trc20Index = buffer.findIndex((price) => price.symbol === "USDT-TRC20")
    const trc = buffer.splice(trc20Index, trc20Index > 0 ? 1 : 0)
    const allPrices = [...trc, ...buffer].filter(
      (price) => !["num", "bch", ...(!isRainCard ? ["usdc-base"] : [])].includes(price.symbol.toLowerCase()),
    )
    return { tableData: buffer, trc, allPrices }
  }, [tableData, isRainCard])
  const [filtered, setFiltered] = useState<typeof allPrices>(allPrices)

  useEffect(() => {
    if (filterValue.length > 0) setFiltered(allPrices.filter((price) => price.symbol.toLowerCase().startsWith(filterValue.toLowerCase())))
    else setFiltered(allPrices)
  }, [filterValue, allPrices])

  const selected = useMemo(() => filtered.find((data) => data.symbol === value?.value) ?? filtered[0], [value, filtered])
  const selectorOptions = useMemo(
    () =>
      filtered.map((opt) => ({
        label: opt.symbol,
        value: opt.symbol,
      })),
    [filtered],
  )

  const tableRows = useMemo(() => {
    return filtered
      .filter((data) => data.symbol.toLowerCase() !== "ltc")
      .map((data) => {
        const isBTC = data.symbol === "BTC"
        const isBase = data.symbol.toLowerCase().includes("usdc-base")
        const fraction = isBTC ? 8 : 5
        return {
          currency: (
            <Fragment>
              <div className="flex gap-2.5 text-cl14 font-bold text-black">
                <img className="w-5 h-5" src={`/client/img/coins/${getCoinIcon(data.symbol)}.svg`} alt="" />
                {data.symbol}
              </div>
              {isBase && (
                <div className="flex items-center gap-2.5 text-red-600">
                  Please make sure this is the
                  <br /> BASE network, not Ethereum
                </div>
              )}
            </Fragment>
          ),
          percent: (
            <span>
              {data.fee}%{data.fix > 0 ? ` + ${data.fix} EUR` : ""}
            </span>
          ),
          fee: (
            <span>
              {data.commissionCrypto.toFixed(fraction)} {data.symbol}
            </span>
          ),
          completion: t({ id: "from6" }),
          spend: (
            <span>
              <strong className="itemSpend">{data.amountCrypto.toFixed(fraction)}</strong> {data.symbol}
            </span>
          ),
          action: (
            <div>
              <Button
                className="w-full"
                onClick={() => {
                  onDepositClick(data.symbol, data.amountCrypto)
                }}
              >
                <FormattedMessage id={loading ? "Loading..." : "deposit"} />
              </Button>
            </div>
          ),
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, t, loading, onDepositClick])

  return (
    <div className="relative">
      {pricesLoading && (
        <div className="loading-content">
          <h3>Loading...</h3>
        </div>
      )}
      <div
        className={clsx("currency-table", {
          "currency-table__loading": pricesLoading,
        })}
      >
        <div className="currency-table__titlebar">
          <Title variant="title">
            <FormattedMessage id={!isCreateCard ? "currencies" : "select-payment"} />
          </Title>
          {isMobile ? (
            <Selector
              leftSection={selected != null && <img className="w-5 h-5" src={`/client/img/coins/${getCoinIcon(selected.symbol)}.svg`} alt="" />}
              value={value ?? selectorOptions[0] ?? null}
              onChange={setValue}
              options={selectorOptions}
              renderOptions={(option, props) => (
                <div {...props}>
                  <img className="w-5 h-5 mr-2" src={`/client/img/coins/${getCoinIcon(option.value)}.svg`} alt="" />
                  {option.label}
                </div>
              )}
              variant="contained"
              classNames={{ control: "w-full" }}
              searchable
            />
          ) : (
            <Input placeholder={t({ id: "filter" })} onChange={(e) => onChange(e.currentTarget.value)} />
          )}
          {value?.value?.toLowerCase() === "usdc-base" && isMobile && (
            <p className="text-red-600 text-sm">Please make sure this is the BASE network, not Ethereum</p>
          )}
        </div>

        {isMobile ? (
          <>
            {selected != null && (
              <div className="px-2 flex flex-col gap-2.5">
                <Row title={t({ id: "fee%" })} value={`${selected?.fee ?? "0"}%${selected.fix > 0 ? ` + ${selected.fix} EUR` : ""}`} />
                <Row title={t({ id: "fee" })} value={`${selected.commissionCrypto} ${selected?.symbol}`} />
                <Row title={t({ id: "completion" })} value={t({ id: "from6" })} />
                <Row title={t({ id: "amount-in-crypto" })} value={`${selected?.amountCrypto} ${selected?.symbol}`} />
              </div>
            )}
            <Button
              className="w-full mt-4"
              onClick={() => {
                if (selected == null) return
                onDepositClick(selected.symbol, selected.amountCrypto)
              }}
            >
              <FormattedMessage id="deposit" />
            </Button>
          </>
        ) : (
          <Table heads={TABLE_HEADS} data={tableRows} classNames={{ row: "currency-table-row", header: "currency-table-head" }} />
        )}
      </div>
    </div>
  )
}
